import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Logo } from "@/components/logo";
import { HomepageDisclaimer } from "@/components/disclaimer-banner";
import { toast } from "sonner";

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Sign in — ForexMind AI" },
      { name: "description", content: "Sign in to your ForexMind AI account to access real-time AI trading signals, the multi-agent council, and your personal dashboard." },
      { property: "og:title", content: "Sign in to ForexMind AI" },
      { property: "og:description", content: "Access your ForexMind AI trading dashboard, real-time signals, and multi-agent council insights." },
      { property: "og:url", content: "https://forexmind.ai/login" },
    ],
    links: [
      { rel: "canonical", href: "https://forexmind.ai/login" },
    ],
  }),
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | undefined;
    void import("@/integrations/supabase/client").then(async ({ supabase }) => {
      const { data } = await supabase.auth.getSession();
      if (active && data.session) navigate({ to: "/dashboard", replace: true });
      const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
        if (session) navigate({ to: "/dashboard", replace: true });
      });
      unsubscribe = () => sub.subscription.unsubscribe();
    });
    return () => {
      active = false;
      unsubscribe?.();
    };
  }, [navigate]);

  const signIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErr(null);
    try {
      const { signInWithLockout } = await import("@/lib/auth-lockout.functions");
      const res = await signInWithLockout({ data: { email, password } });
      if (!res.ok) {
        setErr(res.message);
        return;
      }
      const { supabase } = await import("@/integrations/supabase/client");
      const { error } = await supabase.auth.setSession({
        access_token: res.session.access_token,
        refresh_token: res.session.refresh_token,
      });
      if (error) {
        setErr(error.message);
        return;
      }
      toast.success("Welcome back");
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  const forgot = async () => {
    if (!email) {
      setErr("Enter your email above first");
      return;
    }
    const { supabase } = await import("@/integrations/supabase/client");
    const origin = typeof window === "undefined" ? "" : window.location.origin;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${origin}/reset-password`,
    });
    if (error) toast.error(error.message);
    else toast.success("Password reset email sent");
  };

  return (
    <div
      className="relative min-h-screen overflow-hidden bg-grid"
      style={{ background: "var(--gradient-hero)" }}
    >
      <div className="absolute inset-0" style={{ background: "var(--gradient-glow)" }} />
      <div className="relative">
        <HomepageDisclaimer />
      </div>
      <div className="relative grid place-items-center px-4 py-10">
      <div className="relative w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <Logo size="lg" variant="stacked" />
        </div>
        <Card className="border-border/60 bg-card/80 p-7 backdrop-blur shadow-[var(--shadow-card)]">
          <h1 className="font-display text-2xl font-bold text-foreground">Welcome back, trader</h1>
          <p className="mt-1 text-sm text-muted-foreground">Sign in to access your edge.</p>
          <form onSubmit={signIn} className="mt-6 space-y-4">
            <Field
              label="Email"
              type="email"
              value={email}
              onChange={setEmail}
              placeholder="trader@email.com"
            />
            <Field
              label="Password"
              type="password"
              value={password}
              onChange={setPassword}
              placeholder="••••••••"
            />
            {err && (
              <div className="rounded-md border border-sell/40 bg-sell/10 px-3 py-2 text-xs text-sell">
                {err}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
            <div className="relative my-2 flex items-center gap-3">
              <div className="h-px flex-1 bg-border/60" />
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">or</span>
              <div className="h-px flex-1 bg-border/60" />
            </div>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={async () => {
                const { lovable } = await import("@/integrations/lovable");
                const res = await lovable.auth.signInWithOAuth("google", {
                  redirect_uri: window.location.origin + "/auth/callback",
                });
                if (res.error) toast.error(res.error.message ?? "Google sign-in failed");
              }}
            >
              Continue with Google
            </Button>
            <button
              type="button"
              onClick={forgot}
              className="block w-full text-center text-xs text-muted-foreground hover:text-foreground"
            >
              Forgot password?
            </button>
          </form>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Don't have an account?{" "}
            <Link to="/signup" className="font-semibold text-primary hover:underline">
              Start free
            </Link>
          </p>
        </Card>
      </div>
      </div>
    </div>
  );
}

function Field({
  label,
  type,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required
        className="bg-input/50 font-mono"
      />
    </div>
  );
}
